// frontend/src/pages/NewUIGameDetail.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import axios from '../config/axios';
import NewQuickPlayButton from '../components/newui/NewQuickPlayButton';
import GoogleAdBanner from '../components/newui/GoogleAdBanner';

// Widget de partidas en vivo para la página de un juego
const LiveMatchesWidget = ({ gameSlug, gameName }) => {
    const navigate = useNavigate();
    const [count, setCount] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchCount = useCallback(async () => {
        try {
            const res = await axios.get('/matches/active');
            if (res.data.success) {
                const n = res.data.matches.filter(m => m.game?.slug === gameSlug).length;
                setCount(n);
            }
        } catch {
            setCount(null);
        } finally {
            setLoading(false);
        }
    }, [gameSlug]);

    useEffect(() => {
        fetchCount();
        const id = setInterval(fetchCount, 30000);
        return () => clearInterval(id);
    }, [fetchCount]);

    const haslive = count !== null && count > 0;

    return (
        <div className="relative bg-[#0E2238] rounded-2xl p-8 text-center overflow-hidden shadow-[0_12px_40px_rgba(0,0,0,0.35)] ring-2 ring-white/10 flex flex-col items-center justify-center gap-5 h-full">
            {/* Dot pulsante */}
            <div className="flex items-center justify-center">
                <span className="relative flex h-5 w-5">
                    {haslive && (
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FCE503] opacity-60" />
                    )}
                    <span className={`relative inline-flex rounded-full h-5 w-5 ${haslive ? 'bg-[#FCE503]' : 'bg-white/20'}`} />
                </span>
            </div>

            <div>
                <h2 className="toon text-2xl md:text-3xl text-white mb-2 uppercase">
                    Partidas en Vivo
                </h2>
                <p className="text-white/60 text-sm font-semibold">
                    {loading
                        ? 'Cargando...'
                        : haslive
                            ? <><span className="text-[#FCE503] font-extrabold text-lg">{count}</span> partida{count !== 1 ? 's' : ''} de {gameName} en curso ahora</>
                            : `No hay partidas de ${gameName} activas en este momento`
                    }
                </p>
            </div>

            <button
                onClick={() => navigate(`/active-matches?game=${gameSlug}`)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-[14px] font-bold tracking-wider uppercase text-[#122436] bg-[#FCE503] hover:bg-[#FCE503]/90 transition-colors shadow-[0_6px_20px_rgba(252,229,3,0.3)] text-sm"
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Ver partidas en vivo
            </button>
        </div>
    );
};

const MaskIcon = ({ src, className = "w-5 h-5" }) => (
    <span
        aria-hidden="true"
        className={`inline-block ${className}`}
        style={{
            backgroundColor: "currentColor",
            WebkitMaskImage: `url(${src})`,
            maskImage: `url(${src})`,
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
            maskPosition: "center",
            WebkitMaskSize: "contain",
            maskSize: "contain",
        }}
    />
);

const StatBadge = ({ icon, value, label, className = "" }) => (
    <div className={`flex items-center gap-2 ${className}`}>
        <MaskIcon src={icon} className="w-5 h-5 md:w-6 md:h-6" />
        <span className="text-sm md:text-base font-semibold tracking-wide">
            {value} <span className="uppercase">{label}</span>
        </span>
    </div>
);

const TournamentCard = ({ tournament, onClick }) => {
    const getStatusBadge = (status) => {
        if (status === 'REGISTERING') {
            return (
                <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-extrabold uppercase bg-[#fce503] text-[#0E2238]">
                    Registro Abierto
                </span>
            );
        }
        return (
            <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-extrabold uppercase text-[#fce503] bg-[#122436] border-2 border-[#fce503]">
                En Curso
            </span>
        );
    };

    const formatEstimatedPrize = (entryFee, registeredPlayers) => {
        const estimatedPrize = (entryFee * registeredPlayers) * 0.9;
        return Math.max(estimatedPrize, 0);
    };

    const teamSize = tournament.teamSize || 1;
    const isTeamTournament = teamSize > 1;
    const registeredPlayers = isTeamTournament
        ? (tournament._count.teams || 0) * teamSize
        : tournament._count.players;
    const maxTeams = isTeamTournament ? Math.floor(tournament.maxPlayers / teamSize) : tournament.maxPlayers;
    const teamProgress = isTeamTournament ? tournament._count.teams : tournament._count.players;

    return (
        <div
            onClick={onClick}
            className="relative rounded-[18px] overflow-hidden bg-[#122436] ring-2 ring-white
                shadow-[0_12px_40px_rgba(0,0,0,0.35)] transition-transform duration-200 
                hover:-translate-y-0.5 cursor-pointer min-h-[200px] p-5"
        >
            <div className="flex flex-col h-full text-white/90">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                    <h3 className="text-base md:text-lg font-bold uppercase tracking-wider text-white">
                        {tournament.name}
                    </h3>
                    {getStatusBadge(tournament.status)}
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                        <MaskIcon src="/images/icons/estrella.png" className="w-5 h-5" />
                        <span className="tracking-wide">{tournament.entryFee} tickets</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <MaskIcon src="/images/icons/billete.png" className="w-5 h-5" />
                        <span className="tracking-wide font-bold text-[#fce503]">
                            ~{formatEstimatedPrize(tournament.entryFee, registeredPlayers)} tickets
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <MaskIcon src="/images/icons/users.png" className="w-5 h-5" />
                        <span className="tracking-wide">
                            {teamProgress}/{maxTeams} {isTeamTournament ? 'equipos' : ''}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <MaskIcon src="/images/icons/reloj.png" className="w-5 h-5" />
                        <span className="tracking-wide text-xs">
                            {new Date(tournament.startTime).toLocaleString('es-AR', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const NewUIGameDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [gameData, setGameData] = useState(null);
    const [tournaments, setTournaments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Aplicar clase de fuente
    useEffect(() => {
        document.body.classList.add('acumin-scope');
        document.body.style.overflowX = 'hidden';
        return () => {
            document.body.classList.remove('acumin-scope');
            document.body.style.overflowX = '';
        };
    }, []);

    useEffect(() => {
        const fetchGameData = async () => {
            try {
                const [gameResponse, tournamentsResponse] = await Promise.all([
                    axios.get(`/games/${slug}`),
                    axios.get(`/games/${slug}/tournaments`)
                ]);

                setGameData(gameResponse.data.data);
                setTournaments(tournamentsResponse.data.tournaments);
            } catch (err) {
                setError(err.response?.data?.message || 'Error al cargar los datos');
            } finally {
                setLoading(false);
            }
        };

        fetchGameData();
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#122436] flex items-center justify-center">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#122436] flex items-center justify-center p-4">
                <div className="bg-red-50 text-red-600 p-6 rounded-2xl flex items-center max-w-md shadow-lg">
                    <AlertCircle className="w-6 h-6 mr-3 flex-shrink-0" />
                    <p className="font-semibold">{error}</p>
                </div>
            </div>
        );
    }

    const activeTournaments = tournaments.filter(t => ['REGISTERING', 'IN_PROGRESS'].includes(t.status));

    return (
        <div className="min-h-screen bg-[#122436] overflow-x-hidden">
            <div className="max-w-[calc(100%-100px)] mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12">
                <div className="space-y-12">
                    {/* Game Hero */}
                    <div className="relative bg-[#2284DF] rounded-2xl shadow-lg p-6 md:p-8 overflow-hidden">
                        {/* Textura de fondo */}
                        <div
                            className="absolute inset-0 pointer-events-none z-0"
                            style={{
                                backgroundColor: "#1F79CF",
                                WebkitMaskImage: "url(/images/backgrounds/textura-azul.png)",
                                maskImage: "url(/images/backgrounds/textura-azul.png)",
                                WebkitMaskRepeat: "repeat",
                                maskRepeat: "repeat",
                                WebkitMaskSize: "1769px 1183px",
                                maskSize: "1769px 1183px",
                                WebkitMaskPosition: "center",
                                maskPosition: "center",
                            }}
                            aria-hidden="true"
                        />

                        {/* Contenido */}
                        <div className="relative z-10 flex flex-col lg:flex-row justify-between items-center gap-8">
                            <div className="w-full lg:w-1/2 space-y-6">
                                <img src={gameData.logo} alt={gameData.name} className="h-12 md:h-16 object-contain" />

                                <p className="text-white/90 text-sm md:text-base leading-relaxed">
                                    {gameData.description}
                                </p>

                                <div className="flex flex-wrap items-center gap-6 text-[#fce503]">
                                    <StatBadge
                                        icon="/images/icons/estrella.png"
                                        value={gameData.stats.openTournaments}
                                        label="torneos abiertos"
                                    />
                                    <StatBadge
                                        icon="/images/icons/users.png"
                                        value={gameData.stats.onlinePlayers}
                                        label="jugando ahora"
                                    />
                                </div>
                            </div>

                            <div className="w-full lg:w-1/2">
                                <img
                                    src={gameData.featuredImg}
                                    alt={`${gameData.name} featured`}
                                    className="w-full h-auto object-contain"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Quick Play + Live Matches */}
                    {gameData.status === 'ACTIVE' && (
                        <section className="grid grid-cols-1 md:grid-cols-[3fr_1fr] gap-6">
                            {/* Quick Play */}
                            <div className="relative bg-[#fce503] rounded-2xl p-8 text-center overflow-hidden shadow-[0_12px_40px_rgba(252,229,3,0.35)] flex flex-col items-center justify-center gap-5">
                                <div>
                                    <h2 className="toon text-2xl md:text-3xl text-[#122436] mb-3 uppercase">
                                        ¿Listo para una partida rápida de {gameData.name}?
                                    </h2>
                                    <p className="text-[#122436]/80 text-sm md:text-base font-semibold">
                                        Únete a la cola y arma partida rápida en 1v1, 2v2 o 3v3
                                    </p>
                                </div>
                                <NewQuickPlayButton
                                    variant="ctaBlue"
                                    size="large"
                                    text="Buscar Partida Rápida"
                                />
                            </div>

                            {/* Live Matches */}
                            <LiveMatchesWidget gameSlug={gameData.slug} gameName={gameData.name} />
                        </section>
                    )}

                    {/* Tournaments Section */}
                    <section>
                        <h2 className="toon text-3xl md:text-4xl text-white uppercase tracking-wider mb-6">
                            Torneos Disponibles
                        </h2>

                        {activeTournaments.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {activeTournaments.map(tournament => (
                                    <TournamentCard
                                        key={tournament.id}
                                        tournament={tournament}
                                        onClick={() => navigate(`/tournaments/${tournament.id}`)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="bg-white/95 rounded-2xl p-8 text-center shadow-lg">
                                <div className="mb-6">
                                    <MaskIcon src="/images/icons/estrella.png" className="w-16 h-16 mx-auto mb-4 opacity-30 text-gray-400" />
                                    <h3 className="text-xl font-extrabold text-[#122436] uppercase mb-2">
                                        No hay torneos disponibles
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                        Los torneos se programan regularmente. ¡Vuelve pronto!
                                    </p>
                                </div>

                                {gameData.status === 'ACTIVE' && (
                                    <div className="pt-6 border-t border-gray-200">
                                        <p className="text-sm text-gray-600 mb-4 font-semibold">
                                            Mientras tanto, ¿por qué no pruebas una partida rápida?
                                        </p>
                                        <NewQuickPlayButton
                                            variant="ctaBlue"
                                            size="medium"
                                            text="Juego Rápido"
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </section>

                    <div className="pt-2">
                        <GoogleAdBanner
                            slot={process.env.REACT_APP_ADSENSE_GAME_DETAIL_MID_SLOT || process.env.REACT_APP_ADSENSE_GAME_DETAIL_TOP_SLOT}
                            className="max-w-5xl mx-auto"
                            minHeight={150}
                            compact
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewUIGameDetail;
